import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import { CDM_DEFAULT_CORRECTION_REASON } from "features/cdm/constants/correctionReasonOptions";
import { isCorrectionEligibilityConflict } from "features/cdm/utils/correctionValidation";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useValidateCDM = ({ patientId }) => {
  const queryClient = useQueryClient();

  const handleNetworkError = useHandleNetworkError(
    "Error while validating CDM"
  );
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();
      try {
        const response = await axiosInstance.post(
          `${tenantVal}/cdm/patients/${patientId}/cdm/validate`,
          {
            reviewType: data.reviewType,
            originalReimbursementMessageControlId:
              data?.reimbursementMessageControlId || null,
            correctionReason: data?.reimbursementMessageControlId
              ? CDM_DEFAULT_CORRECTION_REASON
              : null,
            diagnosis: {
              diagnoses: data.registrationReasons,
              yearOfDiagnosis: data.yearOfDiagnosis,
              attendingHospital: data.attendingHospital,
            },
            emergencyAdmissions: {
              edAttendancesLast6Months: data.edAttendancesLast6Months,
              unscheduledAdmissionsLast6Months:
                data.unscheduledAdmissionsLast6Months,
            },
            medicationReview: data.medicationReview === "yes" ? true : false,
            riskFactors: {
              smokingStatus: data.smokingStatus,
              vapingStatus: data.vapingStatus,
              weightKg: Number(data.weight),
              heightCm: Number(data.height),
              bmi: Number(data.bmi),
              waistCircumferenceCm: Number(data.waistCircumference),
              qrisk3Score: data.qRisk3Score != null ? Number(data.qRisk3Score) : 0,
              alcoholAuditCScore: Number(data.alcoholRiskScore),
              alcoholFullAuditScore: data.alcoholFullAuditScore ? Number(data.alcoholFullAuditScore) : null,
              physicalActivityQ1: data.physicalActivityQ1,
              physicalActivityQ2: data.physicalActivityQ2,
              smokingInterventions: (data.smokingInterventions || []).filter(
                Boolean
              ),
              smokingInterventionOther:
                data.smokingInterventions?.includes("OTHER")
                  ? data.smokingInterventionOther || null
                  : null,
              alcoholDependenceInterventions: (
                data.alcoholDependenceInterventions || []
              ).filter(Boolean),
              alcoholHigherRiskInterventions: (
                data.alcoholHigherRiskInterventions || []
              ).filter(Boolean),
              alcoholIncreasingRiskInterventions: (
                data.alcoholIncreasingRiskInterventions || []
              ).filter(Boolean),
              weightHighRiskInterventions: (
                data.weightHighRiskInterventions || []
              ).filter(Boolean),
              weightIncreasedRiskInterventions: (
                data.weightIncreasedRiskInterventions || []
              ).filter(Boolean),
              weightNormalInterventions: (
                data.weightNormalInterventions || []
              ).filter(Boolean),
              physicalActivityInterventions: (
                data.physicalActivityInterventions || []
              ).filter(Boolean),

              influenzaVaccine: data.influenzaVaccine,
              influenzaVaccineDate: data.influenzaVaccineDate
                ? moment(new Date(dayjs(data.influenzaVaccineDate, "DD-MM-YYYY"))).format("YYYY-MM-DD")
                : null,
              pneumococcalVaccine: data.pneumococcalVaccine,
              pneumococcalVaccineDate: data.pneumococcalVaccineDate
                ? moment(new Date(dayjs(data.pneumococcalVaccineDate, "DD-MM-YYYY"))).format("YYYY-MM-DD")
                : null,
              covidVaccine: data.covidVaccine,
              covidVaccineStatusUpToDate:
                data.covidVaccineStatusUpToDate === "YES" ? true : false,
            },
            physicalExam: {
              pulseRate: Number(data.pulse),
              pulseRhythm: data.pulseRhythm,
              systolicBloodPressure: Number(data.bp1),
              diastolicBloodPressure: Number(data.bp2),
            },
            diabetesSpecifics: {
              diabetesRelatedAmputation:
                data.diabetesRelatedAmputation === "YES" ? true : false,
              amputationYear: Number(data.amputationYear),
              footAndLimbReviewFindings: Array.isArray(data?.footAndLimbReviewFindings)
                ? data.footAndLimbReviewFindings.filter(Boolean)
                : [],
              footAndLimbReviewComment: data.footAndLimbReviewComment || null,
              retinaScreeningUpToDate:
                data.retinaScreeningUpToDate === "YES" ? true : false,
              referredForRetinaScreening: data.referredForRetinaScreening || null,
            },
            asthmaCopdSpecifics: {
              exacerbationsLast6Months: data.exacerbationsLast6Months,
            },
            diagnosticInvestigations: {
              ecgSinceLastReview:
                data.ecgSinceLastReview === "YES" ? true : false,
              ecgResult: data.ecgResult || null,
              echoSinceLastReview:
                data.echoSinceLastReview === "YES" ? true : false,
              echoResult: data.echoResult || null,

              spirometrySinceLastReview:
                data.spirometrySinceLastReview === "YES" ? true : false,
              spirometryResult: data.spirometryResult || null,
            },
            laboratoryInvestigations: {
              haemoglobin: Number(data.haemoglobin),
              totalCholesterol: data.totalCholesterol === "NOT_CLINICALLY_NECESSARY" ? "NOT_CLINICALLY_NECESSARY" : Number(data.totalCholesterol),
              hdlCholesterol: data.hdlCholesterol === "NOT_CLINICALLY_NECESSARY" ? "NOT_CLINICALLY_NECESSARY" : Number(data.hdlCholesterol),
              nonHdlCholesterol: Number(data.nonHdlCholesterol),
              ldlCholesterol: data.ldlCholesterol,
              triglycerides: data.triglycerides === "NOT_CLINICALLY_NECESSARY" ? "NOT_CLINICALLY_NECESSARY" : Number(data.triglycerides),
              hba1c: Number(data.hba1c),
              serumCreatinine: Number(data.serumCreatinine),
              egfr: data.egfr,
              tftConducted: data.tftConducted === "YES" ? true : false,
              lftConducted: data.lftConducted === "YES" ? true : false,

              fastingGlucose: data.fastingGlucose,
              creatinineClearance: Number(data.creatinineClearance),
              ntProBNP: data.ntProBNP,
              acr: data.acr === "Not Available" ? "Not Available" : Number(data.acr) || null,
            },
            diseaseAssessmentScores: {
              copdDyspnoeaScore: Number(data.copdDyspnoeaScore),
              cha2ds2vascScore: Number(data.cha2ds2vascScore),
            },
            patientEducation: {
              educationProvided: data.educationProvided || null,
              referredToEducation: data.referredToEducation || null,
              referredToDiabetesPrevention: data.referredToDiabetesPrevention || "NOT_AVAILABLE",
              referralDetails: data.referralDetails || null,
              agreedWrittenCarePlan: data.agreedWrittenCarePlan || null,
            },
            patientDemographics: {
              ethnicityCode: data.ethnicityCode,
            },
            otherMajorDiagnoses: {
              diagnoses: data.otherMajorDiagnoses?.diagnoses ?? [],
              otherText: data.otherMajorDiagnoses?.otherText || null,
            },
            carePlan: data.carePlan,
          }
        );
        return response.data;
      } catch (error) {
        // Editing a saved review: BE validate 409s on eligibility — treat as
        // passed and let submit handle the correction.
        if (isCorrectionEligibilityConflict(error, data)) {
          return { isValid: true };
        }

        return {
          isValid: false,
          validationErrors: [
            error?.response?.data?.error || "Something went wrong",
          ],
        };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-cdm"] });
      queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });
    },
    onError: handleNetworkError,
  });
};
